import React, { ChangeEvent, useCallback, useState } from 'react';

import { team } from '@/assets/icons';
import { AsaphButton } from '@/components/shared/Button';
import { FormInput } from '@/components/shared/Inputs';
import { useUpdateMyTeamMutation } from '@/graphql';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useTeam } from '@/providers/TeamProvider';
import { List, ListItem, ListItemText } from '@material-ui/core';
import { useSnackbar } from 'notistack';

import styles from './ChartRepository.module.scss';

export interface ChartRepositoryProps {
	onSave: () => void;
}

export const ChartRepository: React.FC<ChartRepositoryProps> = ({ onSave }) => {
	const { mutateAsync: updateMyTeam, isLoading } = useUpdateMyTeamMutation();
	const { team, refetch, isLoading: teamLoading } = useTeam();
	const { enqueueSnackbar } = useSnackbar();
	const fireEnterUrlEvent = useAnalyticsEvent('Chart_Repository_Enter_Url');
	const fireSaveEvent = useAnalyticsEvent('Chart_Repository_Save');
	const fireError = useAnalyticsEvent('Error');
	const [fired, setFired] = useState(false);

	const [url, setUrl] = useState<string>(team.chartRepositoryUrl ?? '');

	const saveButtonDisabled =
		url === (team.chartRepositoryUrl ?? '') || isLoading || teamLoading;

	const handleInputChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			setUrl(event.target.value);
			if (!fired) {
				fireEnterUrlEvent();
				setFired(true);
			}
		},
		[fireEnterUrlEvent, fired],
	);

	const handleSubmit = useCallback(async () => {
		try {
			await updateMyTeam({
				input: {
					chartRepositoryUrl: url,
				},
			});
			fireSaveEvent();
		} catch (e) {
			enqueueSnackbar(
				'Something went wrong while saving the chart repository URL',
				{
					variant: 'error',
				},
			);
			fireError({
				name: 'SaveChartlistError',
				details: e,
			});
			return;
		} finally {
			refetch();
			onSave();
		}
	}, [
		enqueueSnackbar,
		onSave,
		refetch,
		updateMyTeam,
		url,
		fireSaveEvent,
		fireError,
	]);

	return (
		<>
			<div className={styles['chart-repository']}>
				<List className={styles['chart-repository__list']}>
					<ListItem
						classes={{ root: styles['chart-repository__header'] }}
					>
						<ListItemText
							primary="Chart repository url"
							classes={{
								primary:
									styles['chart-repository__header-text'],
							}}
						/>
					</ListItem>
					<ListItem
						classes={{ root: styles['chart-repository__input'] }}
					>
						<FormInput
							id="chartRepository"
							name="chartRepository"
							type="text"
							placeholder="Enter url"
							disabled={isLoading}
							fullWidth
							autoComplete="off"
							value={url}
							onChange={handleInputChange}
						/>
						<ListItemText
							secondary="Link your team to cloud storage like Google Drive, or to online charts like SongSelect. Once saved, a “GO TO CHARTS” button will be added to your songs in Songlist."
							classes={{
								secondary: styles['chart-repository__text'],
							}}
						/>
					</ListItem>
				</List>
			</div>
			<div className={styles['chart-repository__action']}>
				<AsaphButton
					color="blue"
					label="Save chart repository"
					onClick={handleSubmit}
					disabled={saveButtonDisabled}
					fullWidth
				/>
			</div>
		</>
	);
};
